import type { MeetingTypeDefinition } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { archiveMeetingType, createMeetingType, unarchiveMeetingType, updateMeetingType } from "./api"

function useMeetingTypesMutationInvalidation() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ["meeting-types"] })
    queryClient.invalidateQueries({ queryKey: ["meetings"] })
    queryClient.invalidateQueries({ queryKey: ["meeting"] })
    queryClient.invalidateQueries({ queryKey: ["prompts"] })
  }
}

export function useCreateMeetingType() {
  const getContext = useApiContext()
  const invalidate = useMeetingTypesMutationInvalidation()

  return useMutation({
    mutationFn: async (meetingType: Partial<MeetingTypeDefinition>) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createMeetingType(context, meetingType)
    },
    onSuccess: invalidate,
  })
}

export function useUpdateMeetingType() {
  const getContext = useApiContext()
  const invalidate = useMeetingTypesMutationInvalidation()

  return useMutation({
    mutationFn: async ({ id, meetingType }: { id: string; meetingType: Partial<MeetingTypeDefinition> }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateMeetingType(context, id, meetingType)
    },
    onSuccess: invalidate,
  })
}

export function useArchiveMeetingType() {
  const getContext = useApiContext()
  const invalidate = useMeetingTypesMutationInvalidation()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return archiveMeetingType(context, id)
    },
    onSuccess: invalidate,
  })
}

export function useUnarchiveMeetingType() {
  const getContext = useApiContext()
  const invalidate = useMeetingTypesMutationInvalidation()

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return unarchiveMeetingType(context, id)
    },
    onSuccess: invalidate,
  })
}
